import React, { useState } from 'react';
import GenericCRUDPage from '../../components/GenericCRUDPage';
import { Modal, Button, FormField, Input, Select, Textarea } from '../../components/UI';
import JalaliDatePicker from '../../components/JalaliDatePicker';
import { customerService, accountingService } from '../../api/services';
import { formatCurrency } from '../../utils/helpers';
import { toJalali } from '../../utils/jalali';
import { Eye, DollarSign, TrendingUp, ShoppingCart, CreditCard } from 'lucide-react';
import toast from 'react-hot-toast';

const CustomerFormFields = ({ form: { register, watch, setValue, formState: { errors } } }) => (
  <div className="grid grid-cols-2 gap-4">
    <FormField label="Full Name" required error={errors.name?.message}>
      <Input {...register('name', { required: 'Required' })} placeholder="Customer name" />
    </FormField>
    <FormField label="Phone">
      <Input {...register('phone')} placeholder="+93 700 000 000" />
    </FormField>
    <FormField label="Email">
      <Input type="email" {...register('email')} placeholder="email@example.com" />
    </FormField>
    <FormField label="Date of Birth">
      <JalaliDatePicker value={watch('dateOfBirth')||''} onChange={v=>setValue('dateOfBirth',v)} placeholder="Date of birth" className="w-full"/>
    </FormField>
    <FormField label="Gender">
      <Select {...register('gender')}>
        <option value="">Select gender</option>
        <option value="male">Male</option>
        <option value="female">Female</option>
        <option value="other">Other</option>
      </Select>
    </FormField>
    <div className="col-span-2">
      <FormField label="Address">
        <Textarea {...register('address')} placeholder="Address" rows={2} />
      </FormField>
    </div>
  </div>
);

const columns = [
  { key: 'name', label: 'Name', sortable: true },
  { key: 'phone', label: 'Phone' },
  { key: 'email', label: 'Email' },
  { key: 'gender', label: 'Gender', render: (v) => v ? <span className="capitalize">{v}</span> : '-' },
  { key: 'totalDebt', label: 'Balance Due', render: (v) => v > 0 ? <span className="text-red-600 font-semibold">{formatCurrency(v)}</span> : <span className="text-gray-400">—</span> },
  { key: 'createdAt', label: 'Registered', render: (v) => toJalali(v) },
];

// ── Customer Profile Modal ─────────────────────────────────────────────────────
function CustomerProfileModal({ customer, onClose }) {
  const [profile, setProfile]     = useState(null);
  const [loading, setLoading]     = useState(true);
  const [payForm, setPayForm]     = useState({ amount:'', method:'cash', notes:'' });
  const [paying, setPaying]       = useState(false);
  const [showPayForm, setShowPayForm] = useState(false);

  React.useEffect(() => {
    if (!customer) return;
    setLoading(true);
    accountingService.getCustomerProfile(customer._id)
      .then(r => setProfile(r.data.data))
      .catch(() => toast.error('Failed to load profile'))
      .finally(() => setLoading(false));
  }, [customer?._id]);

  const handlePay = async () => {
    if (!payForm.amount || Number(payForm.amount) <= 0) return toast.error('Enter a valid amount');
    setPaying(true);
    try {
      await accountingService.addCustomerPayment(customer._id, { amount: Number(payForm.amount), method: payForm.method, notes: payForm.notes });
      toast.success('Payment recorded');
      setPayForm({ amount:'', method:'cash', notes:'' });
      setShowPayForm(false);
      // reload
      const r = await accountingService.getCustomerProfile(customer._id);
      setProfile(r.data.data);
    } catch(e) { toast.error(e.response?.data?.message || 'Failed'); }
    finally { setPaying(false); }
  };

  if (!customer) return null;

  return (
    <Modal isOpen={!!customer} onClose={onClose} title={`Profile — ${customer.name}`} size="xl">
      {loading ? (
        <div className="flex items-center justify-center py-16 text-gray-400">Loading...</div>
      ) : profile ? (
        <div className="space-y-5">
          {/* Stats Cards */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div className="bg-blue-50 rounded-xl p-3 text-center">
              <ShoppingCart size={18} className="mx-auto text-blue-500 mb-1"/>
              <p className="text-xl font-bold text-blue-700">{profile.stats.visitCount}</p>
              <p className="text-xs text-gray-500">Total Visits</p>
            </div>
            <div className="bg-green-50 rounded-xl p-3 text-center">
              <TrendingUp size={18} className="mx-auto text-green-500 mb-1"/>
              <p className="text-lg font-bold text-green-700">{formatCurrency(profile.stats.totalPurchased)}</p>
              <p className="text-xs text-gray-500">Total Purchased</p>
            </div>
            <div className="bg-purple-50 rounded-xl p-3 text-center">
              <DollarSign size={18} className="mx-auto text-purple-500 mb-1"/>
              <p className="text-lg font-bold text-purple-700">{formatCurrency(profile.stats.totalPaid)}</p>
              <p className="text-xs text-gray-500">Total Paid</p>
            </div>
            <div className={`rounded-xl p-3 text-center ${profile.stats.totalDebt > 0 ? 'bg-red-50' : 'bg-gray-50'}`}>
              <CreditCard size={18} className={`mx-auto mb-1 ${profile.stats.totalDebt > 0 ? 'text-red-500' : 'text-gray-400'}`}/>
              <p className={`text-lg font-bold ${profile.stats.totalDebt > 0 ? 'text-red-600' : 'text-gray-400'}`}>{formatCurrency(profile.stats.totalDebt)}</p>
              <p className="text-xs text-gray-500">Balance Due</p>
            </div>
          </div>

          {/* Customer Info */}
          <div className="grid grid-cols-3 gap-2 text-sm bg-gray-50 rounded-xl p-3">
            <div><span className="text-gray-400 text-xs">Phone</span><p>{profile.customer.phone||'-'}</p></div>
            <div><span className="text-gray-400 text-xs">Email</span><p>{profile.customer.email||'-'}</p></div>
            <div><span className="text-gray-400 text-xs">Last Visit</span><p>{toJalali(profile.stats.lastVisit)||'-'}</p></div>
          </div>

          {/* Record Payment */}
          {profile.stats.totalDebt > 0 && (
            <div>
              {!showPayForm ? (
                <Button onClick={()=>setShowPayForm(true)} className="w-full bg-green-600 hover:bg-green-700 text-white">
                  <DollarSign size={15}/> Record Payment (Balance: {formatCurrency(profile.stats.totalDebt)})
                </Button>
              ) : (
                <div className="border border-green-200 bg-green-50 rounded-xl p-4 space-y-3">
                  <p className="text-sm font-semibold text-green-800">Record Payment</p>
                  <div className="grid grid-cols-2 gap-3">
                    <FormField label="Amount">
                      <Input type="number" step="0.01" min="0" max={profile.stats.totalDebt}
                        value={payForm.amount} onChange={e=>setPayForm(p=>({...p,amount:e.target.value}))} placeholder="0"/>
                    </FormField>
                    <FormField label="Method">
                      <Select value={payForm.method} onChange={e=>setPayForm(p=>({...p,method:e.target.value}))}>
                        <option value="cash">Cash</option>
                        <option value="bank">Bank</option>
                        <option value="card">Card</option>
                        <option value="mobile">Mobile</option>
                      </Select>
                    </FormField>
                  </div>
                  <FormField label="Notes">
                    <Input value={payForm.notes} onChange={e=>setPayForm(p=>({...p,notes:e.target.value}))} placeholder="Optional note"/>
                  </FormField>
                  <div className="flex gap-2">
                    <button onClick={()=>setPayForm(p=>({...p,amount:String(profile.stats.totalDebt)}))}
                      className="text-xs px-3 py-1.5 bg-white border border-green-300 text-green-700 rounded-lg hover:bg-green-50">
                      Pay Full ({formatCurrency(profile.stats.totalDebt)})
                    </button>
                    <Button onClick={handlePay} loading={paying} className="ml-auto bg-green-600 hover:bg-green-700 text-white">Confirm</Button>
                    <Button variant="secondary" onClick={()=>setShowPayForm(false)}>Cancel</Button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Recent Purchases */}
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase mb-2">Recent Purchases ({profile.sales.length})</p>
            <div className="border border-gray-100 rounded-xl overflow-hidden max-h-48 overflow-y-auto">
              {profile.sales.length === 0 ? (
                <p className="text-sm text-gray-400 text-center py-6">No purchases yet</p>
              ) : (
                <table className="w-full text-xs">
                  <thead className="bg-gray-50 sticky top-0"><tr>
                    <th className="text-left px-3 py-2 font-semibold text-gray-400">Invoice</th>
                    <th className="text-left px-3 py-2 font-semibold text-gray-400">Date</th>
                    <th className="text-right px-3 py-2 font-semibold text-gray-400">Total</th>
                    <th className="text-right px-3 py-2 font-semibold text-gray-400">Paid</th>
                    <th className="text-left px-3 py-2 font-semibold text-gray-400">Status</th>
                  </tr></thead>
                  <tbody className="divide-y divide-gray-50">
                    {profile.sales.map(s=>(
                      <tr key={s._id} className="hover:bg-gray-50">
                        <td className="px-3 py-2 font-mono text-blue-700">{s.invoiceNumber}</td>
                        <td className="px-3 py-2 text-gray-400">{toJalali(s.saleDate||s.createdAt)}</td>
                        <td className="px-3 py-2 text-right font-semibold">{formatCurrency(s.totalAmount)}</td>
                        <td className="px-3 py-2 text-right text-green-600">{formatCurrency(s.paidAmount)}</td>
                        <td className="px-3 py-2">
                          <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${
                            s.paymentStatus==='paid'?'bg-green-100 text-green-700':
                            s.paymentStatus==='partial'?'bg-blue-100 text-blue-700':
                            'bg-red-100 text-red-700'}`}>
                            {s.paymentStatus}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>

          {/* Payment History */}
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase mb-2">Payment History ({profile.payments.length})</p>
            <div className="border border-gray-100 rounded-xl overflow-hidden max-h-36 overflow-y-auto">
              {profile.payments.length === 0 ? (
                <p className="text-sm text-gray-400 text-center py-4">No payments yet</p>
              ) : (
                <table className="w-full text-xs">
                  <thead className="bg-gray-50 sticky top-0"><tr>
                    <th className="text-left px-3 py-2 font-semibold text-gray-400">Date</th>
                    <th className="text-right px-3 py-2 font-semibold text-gray-400">Amount</th>
                    <th className="text-left px-3 py-2 font-semibold text-gray-400">Method</th>
                    <th className="text-left px-3 py-2 font-semibold text-gray-400">Notes</th>
                  </tr></thead>
                  <tbody className="divide-y divide-gray-50">
                    {profile.payments.map(p=>(
                      <tr key={p._id} className="hover:bg-gray-50">
                        <td className="px-3 py-2 text-gray-400">{toJalali(p.createdAt)}</td>
                        <td className="px-3 py-2 text-right font-semibold text-green-700">{formatCurrency(p.amount)}</td>
                        <td className="px-3 py-2 capitalize text-gray-600">{p.method||'-'}</td>
                        <td className="px-3 py-2 text-gray-400">{p.notes||'-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>

          <div className="flex justify-end">
            <Button variant="secondary" onClick={onClose}>Close</Button>
          </div>
        </div>
      ) : (
        <p className="text-center text-gray-400 py-8">Failed to load profile</p>
      )}
    </Modal>
  );
}

// ── Main Page ──────────────────────────────────────────────────────────────────
export default function CustomersPage() {
  const [profileCustomer, setProfileCustomer] = useState(null);

  const extraActions = (row) => (
    <button onClick={() => setProfileCustomer(row)}
      className="p-1.5 rounded hover:bg-blue-50 text-gray-400 hover:text-blue-600" title="View Profile">
      <Eye size={14}/>
    </button>
  );

  return (
    <>
      <GenericCRUDPage
        title="Customers"
        subtitle="Manage customer records"
        service={customerService}
        entityName="Customer"
        columns={columns}
        FormFields={CustomerFormFields}
        modalSize="lg"
        extraActions={extraActions}
      />

      <CustomerProfileModal
        customer={profileCustomer}
        onClose={() => setProfileCustomer(null)}
      />
    </>
  );
}
